import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getRememberedEmail,
  login,
  loginWithMicrosoft,
  microsoftSsoConfigured,
  resendVerificationEmail,
} from '../../shared/api/auth';
import { ApiError } from '../../shared/api/client';
import { useAuthStore } from '../../shared/hooks/useAuthStore';
import { ALLOWED_INVITE_DOMAINS } from '@shared/contracts/invite';
import { ERROR_CODES } from '@shared/contracts/errors';

/**
 * Login page - fully static. No motion, no animated SVG, no cursor-follow,
 * no backdrop-filter on the card. Background is a single CSS gradient that
 * the browser paints once and never repaints. Repeatedly the cheapest
 * thing the page can be while still on-brand.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(() => getRememberedEmail() ?? '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => !!getRememberedEmail());
  const [error, setError] = useState<string | null>(null);
  // Tracks whether the most recent error was specifically the unverified-email
  // case so we can offer a contextual "Resend verification email" CTA without
  // string-matching the error message.
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);
  const [ssoConfigured, setSsoConfigured] = useState<boolean | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

  // Banner cues from the auth round-trip URLs:
  //   /login?verify=success|expired|invalid  → email verification landing
  //   /login?reset=success                   → password just reset
  const verifyState = params.get('verify');
  const resetState = params.get('reset');

  useEffect(() => {
    void microsoftSsoConfigured()
      .then(setSsoConfigured)
      .catch(() => setSsoConfigured(false));
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setUnverifiedEmail(null);
    setResendStatus('idle');
    setBusy(true);
    try {
      const res = await login(email, password, rememberMe);
      // Wipe any persisted React Query cache from a previous session/user.
      // Without this, the new user briefly sees the previous user's data
      // hydrated from IndexedDB before the first refetch lands.
      queryClient.clear();
      setUser(res.user);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.body.message);
        if (err.body.code === ERROR_CODES.AUTH_EMAIL_NOT_VERIFIED) {
          setUnverifiedEmail(email.trim().toLowerCase());
        }
      } else setError('Login failed');
    } finally {
      setBusy(false);
    }
  }

  async function onResendVerification() {
    if (!unverifiedEmail) return;
    setResendStatus('sending');
    try {
      await resendVerificationEmail(unverifiedEmail);
    } catch {
      // The endpoint is enumeration-proof and always 200; if the fetch itself
      // fails (network), we still tell the user it was sent so they can try
      // again. The cost of a misleading message here is low.
    }
    setResendStatus('sent');
  }

  async function onMicrosoftSignIn() {
    setError(null);
    setSsoBusy(true);
    try {
      const res = await loginWithMicrosoft(true);
      // Same rationale as the password path - clear stale cache from any
      // previous identity before mounting the shell.
      queryClient.clear();
      setUser(res.user);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.body.message);
      else if (err instanceof Error) setError(err.message);
      else setError('Microsoft sign-in failed');
    } finally {
      setSsoBusy(false);
    }
  }

  return (
    <div
      className="relative min-h-full w-full flex flex-col lg:flex-row"
      style={{
        // Static gradient - painted once, no animation, no repaints. Hex
        // colours match the brand: deep slate base, hint of navy and cyan
        // at the diagonals.
        background:
          'linear-gradient(135deg, #0f172a 0%, #11233f 55%, #0e2a3a 100%)',
      }}
    >
      {/* Left brand panel — hidden on narrow viewports so the form takes
          the whole window. On lg+ it occupies the left ~45% with the
          form on the right ~55%. The slight bias toward the form keeps
          the eye anchored on the actionable area. */}
      <aside className="hidden lg:flex lg:basis-[45%] lg:shrink-0 items-center justify-center px-10 py-12">
        <div className="flex flex-col items-center text-center max-w-sm">
          <div className="rounded-2xl bg-white p-6 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.5)]">
            <img
              src="/tricorian-logo.png"
              alt="Tricorian"
              width="200"
              height="200"
              style={{ display: 'block', width: '200px', height: 'auto' }}
            />
          </div>
          <div className="mt-7 text-2xl font-semibold text-slate-100 tracking-tight">
            Tricorian CRM
          </div>
          <div className="mt-2 text-[12px] tracking-wider text-slate-400 uppercase">
            Healthcare investment · Local-first · Encrypted
          </div>
          <div className="mt-6 h-px w-24 bg-slate-700/60" />
          <p className="mt-6 text-sm leading-relaxed text-slate-400 max-w-xs">
            Pipeline, qualification, due-diligence and contract tracking in one
            place — built for the way Tricorian works.
          </p>
        </div>
      </aside>

      {/* Subtle vertical separator on lg+ — a single 1px column, no shadows
          or transitions, so it costs nothing at runtime. */}
      <div className="hidden lg:block w-px self-stretch bg-white/[0.06]" />

      {/* Right form panel. */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900/95 p-8 shadow-soft-lg">
          {/* Small in-card brand header — only visible when the left panel
              is hidden (narrow windows), so the user still sees branding. */}
          <div className="flex items-center gap-3 mb-6 lg:hidden">
            <img
              src="/tricorian-logo.png"
              alt="Tricorian"
              width="40"
              height="40"
              style={{ display: 'block', width: '40px', height: 'auto', background: '#fff', borderRadius: 8, padding: 2 }}
            />
            <div>
              <div className="text-base font-semibold text-slate-100">
                Tricorian CRM
              </div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-accent-300/80">
                Sign in
              </div>
            </div>
          </div>
          <div className="hidden lg:block mb-6">
            <h1 className="text-xl font-semibold text-slate-100">Welcome back</h1>
            <p className="text-sm text-slate-400 mt-1">Sign in to continue.</p>
          </div>

        {verifyState === 'success' && (
          <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
            Email verified. You can sign in below.
          </div>
        )}
        {verifyState === 'expired' && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
            That verification link has expired. Sign in and we&apos;ll send a new one.
          </div>
        )}
        {verifyState === 'invalid' && (
          <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
            That verification link is no longer valid. Sign in and request a new one.
          </div>
        )}
        {resetState === 'success' && (
          <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
            Password updated. Sign in with your new password.
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <p className="text-xs text-slate-400">
              Restricted to{' '}
              <span className="text-accent-300/90 font-medium">
                {ALLOWED_INVITE_DOMAINS.join(', ')}
              </span>
            </p>
          </div>

          <label className="block">
            <div className="label mb-1">Email</div>
            <input
              type="email"
              autoComplete="username"
              className="input"
              placeholder="name@tricorian.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>

          <label className="block">
            <div className="label mb-1">Password</div>
            <input
              type="password"
              autoComplete="current-password"
              className="input"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-300 select-none cursor-pointer">
            <input
              type="checkbox"
              className="accent-brand-500"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>
              Remember me on this device
              <span className="block text-[11px] text-slate-500">
                Keeps you signed in for 30 days. Don&apos;t tick on shared computers.
              </span>
            </span>
          </label>

          {error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
              <div>{error}</div>
              {unverifiedEmail && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {resendStatus === 'sent' ? (
                    <span className="text-emerald-300 text-xs">
                      ✓ Verification email sent - check your inbox.
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="text-xs underline text-rose-200 hover:text-rose-100 disabled:opacity-50"
                      disabled={resendStatus === 'sending'}
                      onClick={() => void onResendVerification()}
                    >
                      {resendStatus === 'sending'
                        ? 'Sending…'
                        : 'Resend verification email'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-end -mt-1">
            <Link
              to="/forgot-password"
              className="text-[11px] text-slate-400 hover:text-accent-300 transition-colors"
            >
              Forgot password?
            </Link>
          </div>

          <button type="submit" className="btn-primary w-full" disabled={busy || ssoBusy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <div className="flex items-center gap-3 my-1">
            <span className="flex-1 h-px bg-slate-700" />
            <span className="text-[10px] uppercase tracking-wider text-slate-500">or</span>
            <span className="flex-1 h-px bg-slate-700" />
          </div>

          <button
            type="button"
            className="w-full inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg border border-slate-300 bg-white text-slate-800 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            disabled={busy || ssoBusy || ssoConfigured === false}
            onClick={onMicrosoftSignIn}
            title={
              ssoConfigured === false
                ? 'Microsoft SSO is not configured on this server. Set AZURE_TENANT_ID and AZURE_CLIENT_ID before launch.'
                : undefined
            }
          >
            <MicrosoftLogo />
            {ssoBusy
              ? 'Opening Microsoft sign-in…'
              : ssoConfigured === false
                ? 'Microsoft SSO (not configured)'
                : 'Sign in with Microsoft'}
          </button>
        </form>

          <p className="mt-5 text-xs text-slate-400 text-center">
            New to Tricorian?{' '}
            <Link to="/signup" className="link-brand">
              Create an account
            </Link>
          </p>
          <p className="mt-3 text-[11px] text-slate-500 text-center lg:hidden">
            Built by Tricorian Life Pty Ltd
          </p>
          <p className="mt-4 pt-3 border-t border-white/[0.06] text-[10px] text-slate-600 text-center hidden lg:block">
            Built by Tricorian Life Pty Ltd · © 2026
          </p>
        </div>
      </main>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
      <rect x="0" y="0" width="10" height="10" fill="#F25022" />
      <rect x="13" y="0" width="10" height="10" fill="#7FBA00" />
      <rect x="0" y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}
